import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  IconButton,
  Divider,
  Skeleton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { isUserSuperAdminSelector, CurrentUserSelector } from 'app/selectors/userSelectors';
import { addInfo, addError } from 'app/actions/snackbar/snackbar';
import { usePersonaGetPersona, usePersonaDeletePersona } from '@/api-gen/api';
import type { PersonaDto } from '@/api-gen/api.schemas';
import { AccessLevel } from '@/api-gen/api.schemas';
import type { AccessLevel as AccessLevelType } from '@/api-gen/api.schemas';
import PersonaForm, { type PersonaFormHandle } from './PersonaForm';
import SharedCompaniesDisplay, { SharedCompaniesDisplayRef } from '../SharedCompaniesDisplay/SharedCompaniesDisplay';
import PersonaScenariosDisplay from './PersonaScenariosDisplay';
import ShareWithCompanyDialog from '../ShareWithCompanyDialog/ShareWithCompanyDialog';
import DeletePersonaDialog from './DeletePersonaDialog';
import type { I18nPersonasNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import { useTranslation } from 'react-i18next';
import { personaChatUiOpen, personaChatIsLoading } from '@/features/personaChat/state/signals.ts';


interface PersonaDetailDialogProps {
  open: boolean;
  onClose: () => void;
  personaId?: number;
  onRefresh?: () => void;
  onPersonaUpdated?: (persona: PersonaDto) => void;
}

const PersonaDetailDialog: React.FC<PersonaDetailDialogProps> = ({
  open,
  onClose,
  personaId,
  onRefresh,
  onPersonaUpdated
}) => {
  const dispatch = useDispatch();
  const isSuperAdmin = useSelector(isUserSuperAdminSelector);
  const currentUser = useSelector(CurrentUserSelector);
  const sharedCompaniesRef = useRef<SharedCompaniesDisplayRef>(null);
  const personaFormRef = useRef<PersonaFormHandle>(null);
  
  const { t: translate } = useTranslation(I18nNamespace.Personas);
  const { t: translateCommon } = useTranslation(I18nNamespace.Common);
  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const personaChatOpen = personaChatUiOpen.value;
  const personaChatLoading = personaChatIsLoading.value;
  
  // Fetch persona details
  const { data: personaResponse, isLoading, error, refetch } = usePersonaGetPersona(
    personaId!,
    {
      query: {
        enabled: !!personaId && open,
        refetchOnMount: 'always',  // Always refetch when dialog opens
        staleTime: 0  // Consider data immediately stale
      }
    }
  );

  const persona = personaResponse?.data || personaResponse;

  const rawOtherCompaniesCount = (persona as Partial<PersonaDto> | undefined)?.otherCompaniesCount;
  const isPersonaShared = typeof rawOtherCompaniesCount === 'number' && rawOtherCompaniesCount > 0;
  
  // Type guard to ensure we have a valid persona
  const isPersonaDto = (p: any): p is PersonaDto => p && typeof p.id === 'number';

  // Delete mutation
  const deleteMutation = usePersonaDeletePersona({
    mutation: {
      onSuccess: () => {
        dispatch(addInfo({ infoText: translate(nameof.full<I18nPersonasNs>((n) => n.messages.personaDeletedSuccess)) }));
        onRefresh?.();
        onClose();
      },
      onError: (error: any) => {
        dispatch(addError({ errorText: error?.message || translate(nameof.full<I18nPersonasNs>((n) => n.messages.deletePersonaError)) }));
      }
    }
  });


  const handleCancelEdit = () => {
    onClose();
  };

  const handleSaveEdit = (updatedPersona?: PersonaDto) => {
    // Optimistically notify parent with updated persona (if provided)
    if (updatedPersona) {
      onPersonaUpdated?.(updatedPersona);
    }
    // PersonaForm handles cache; still notify parent to refresh if needed
    onRefresh?.();
    onClose();
  };

  const handleDelete = () => {
    if (personaId) {
      deleteMutation.mutate({ id: personaId });
      setIsDeleteDialogOpen(false);
    }
  };

  const personaChatButtonLabel = personaChatLoading
    ? translate(nameof.full<I18nPersonasNs>((n) => n.chat.buttons.startingPersonaChat))
    : personaChatOpen
    ? translate(nameof.full<I18nPersonasNs>((n) => n.chat.buttons.stopPersonaChat))
    : translate(nameof.full<I18nPersonasNs>((n) => n.chat.buttons.testPersona));

  const handleShare = () => {
    setIsShareDialogOpen(true);
  };

  const handleTogglePersonaChat = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!personaChatOpen) {
      personaFormRef.current?.startPersonaChat();
    } else {
      personaFormRef.current?.closePersonaChat();
    }
  };

  const validPersona = isPersonaDto(persona) ? persona : null;
  
  // Utility function to get user's access level to the persona
  const getUserAccessLevel = (persona: PersonaDto): AccessLevelType | undefined => {
    // Superadmins always have full access
    if (isSuperAdmin) return AccessLevel.CanAdministrate;
    
    // Regular users: find their company's access in the array
    const userCompanyId = currentUser?.companyId;
    if (!userCompanyId) return undefined;
    
    const companyAccess = persona.companies?.find(c => c.companyId === userCompanyId);
    return companyAccess?.access;
  };
  
  // Determine user's access level and permissions
  const accessLevel = validPersona ? getUserAccessLevel(validPersona) : undefined;
  const canEdit = accessLevel === AccessLevel.CanEdit || accessLevel === AccessLevel.CanAdministrate;
  const canView = accessLevel === AccessLevel.CanRead;
  const canDelete = accessLevel === AccessLevel.CanAdministrate;
  const canShare = accessLevel === AccessLevel.CanAdministrate;
  const otherOrganizationsCount = Math.max(0, validPersona?.otherCompaniesCount ?? 0);
  
  // Effect to track form dirty state
  useEffect(() => {
    const interval = setInterval(() => {
      if (personaFormRef.current) {
        setIsFormDirty(personaFormRef.current.isDirty);
      }
    }, 500); // Check every 500ms
    
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" component="h2">
              {translate(nameof.full<I18nPersonasNs>((n) => n.detailDialog.editPersona))}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                type="button"
                variant="contained"
                size="medium"
                color="primary"
                onClick={handleTogglePersonaChat}
                disabled={personaChatLoading || isLoading}
                startIcon={
                  personaChatLoading
                    ? <CircularProgress size={18} color="inherit" />
                    : (personaChatOpen ? <StopIcon /> : <PlayArrowIcon />)
                }
                sx={{ minWidth: 150 }}
              >
                {personaChatButtonLabel}
              </Button>
              {validPersona && canShare && (
                <IconButton onClick={handleShare} size="small">
                  <ShareIcon />
                </IconButton>
              )}
              {validPersona && canDelete && (
                <IconButton 
                  onClick={() => setIsDeleteDialogOpen(true)} 
                  size="small"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              )}
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box p={3}>
              <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={200} />
            </Box>
          ) : (error !== undefined && error !== null) ? (
            <Box p={3}>
              <Alert severity="error">
                {translate(nameof.full<I18nPersonasNs>((n) => n.messages.loadPersonaDetailsError))}
              </Alert>
            </Box>
          ) : (
            <Grid container sx={{ height: '100%' }}>
              <Grid item xs={12} md={8}>
                <Box px={3} py={2}>
                  {validPersona && (canEdit || canView) ? (
                    <PersonaForm
                      ref={personaFormRef}
                      persona={validPersona}
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                      readOnly={!canEdit}
                    />
                  ) : (
                    <Box p={3}>
                      <Alert severity="warning">
                        {translateCommon(nameof.full<I18nCommonNs>((n) => n.fallbackPage.unauthorizedAccessMessage))}
                      </Alert>
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4} sx={{ borderLeft: 1, borderColor: 'divider' }}>
                <Box p={3}>
                  <Typography variant="h6" gutterBottom>
                    {translate(nameof.full<I18nPersonasNs>((n) => n.detailDialog.sharingSettings))}
                  </Typography>
                  <SharedCompaniesDisplay
                    ref={sharedCompaniesRef}
                    personaId={personaId}
                    otherOrganizationsCount={otherOrganizationsCount}
                    onRefresh={() => refetch()}
                  />

                  {/* Scenarios using this persona - only show for users who can edit/delete */}
                  {canEdit && (
                    <Box sx={{ mt: 3 }}>
                      <PersonaScenariosDisplay personaId={personaId} />
                    </Box>
                  )}

                  {/* Warning for editing shared persona */}
                  {isPersonaShared && isFormDirty && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {translate(nameof.full<I18nPersonasNs>((n) => n.detailDialog.sharedPersonaEditWarning))}
                    </Alert>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        {validPersona && (canEdit || canView) && (
          <>
            <Divider />
            <DialogActions>
              <Button onClick={handleCancelEdit} startIcon={canEdit ? <CancelIcon /> : <CloseIcon />}>
                {canEdit
                  ? translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))
                  : translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.close))}
              </Button>
              {canEdit && (
                <Button
                  variant="contained"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    personaFormRef.current?.submit();
                  }}
                  startIcon={<SaveIcon />}
                >
                  {translate(nameof.full<I18nPersonasNs>((n) => n.detailDialog.saveChanges))}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <DeletePersonaDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        personaId={personaId!}
        personaName={validPersona?.name}
        userRole={isSuperAdmin ? 'SuperAdmin' : 'Admin'}
      />

      <ShareWithCompanyDialog
        open={isShareDialogOpen}
        onClose={() => {
          setIsShareDialogOpen(false);
          refetch();
          sharedCompaniesRef.current?.refetch();
          onRefresh?.();
        }}
        personaId={personaId}
      />
    </>
  );
};

export default PersonaDetailDialog;
